import { useState, useEffect, useRef } from 'react';
import { Template } from '../../core/template.schemas';
import { useGoogleFonts } from './useGoogleFonts';
import { resolveStaticOrDynamicWithPreview } from '../../core/runtime/resolve';
import { fetchImageBlobUrl } from '../../hooks/useCacheBustedImage';

interface TemplateAssetsState {
  loaded: boolean;
  progress: number; // 0-100
  error: string | null;
}

/**
 * Hook to preload all assets (fonts and images) for a template.
 * @param template The template to check for assets
 * @returns State object with loaded status and progress
 */
export const useTemplateAssets = (template: Template | null): TemplateAssetsState => {
  const [state, setState] = useState<TemplateAssetsState>({
    loaded: false,
    progress: 0,
    error: null,
  });

  const { ensureTemplateFontsLoaded } = useGoogleFonts();
  // Ref to track if we've already started loading for this specific template ID/revision to avoid double-loading
  // We'll use a simple string key combination of template ID + elements length/hash approx for now
  const loadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!template) {
      setState({ loaded: true, progress: 100, error: null });
      return;
    }

    // Generate a simple key to detect if we need to reload
    // In a real app, maybe check a revision ID, but here we can check template ID
    const currentKey = template.id;

    // If we're already loaded for this template, don't restart unless ID changes
    // This assumes editing doesn't require full reload every change, which is correct (assets load incrementally usually)
    // But for initial load, we want to block.
    if (state.loaded && loadRef.current === currentKey) {
      return;
    }

    let isMounted = true;
    loadRef.current = currentKey;

    const loadAssets = async () => {
      setState(prev => ({ ...prev, loaded: false, progress: 0, error: null }));
      
      try {
        // 1. Collect all assets
        const imageUrls = new Set<string>();
        
        // Scan elements for images
        template.elements.forEach(el => {
          if (el.type === 'image' && el.source) {
            // Use resolver to get the URL (handling static and dynamic defaults)
            // We force cast to string because for images, the resolver returns the URL string
            const url = resolveStaticOrDynamicWithPreview(el.source, { selected: {}, overrides: {} }) as unknown as string;
            if (url && typeof url === 'string' && url !== '/defaults/image_placeholder.png') {
              imageUrls.add(url);
            }
          }
          if (el.type === 'svg' && el.source) { 
             // SVGs also use 'source' property
             const url = resolveStaticOrDynamicWithPreview(el.source, { selected: {}, overrides: {} }) as unknown as string;
             if (url && typeof url === 'string' && url !== '/defaults/image_placeholder.png') {
               imageUrls.add(url);
             }
          }
        });

        const urls = Array.from(imageUrls);
        const totalItems = urls.length + 1; // +1 for fonts
        let completedItems = 0;

        const updateProgress = () => {
          if (!isMounted) return;
          const p = Math.round((completedItems / totalItems) * 100);
          setState(prev => ({ ...prev, progress: p }));
        };

        // 2. Load Fonts (with safety timeout wrapper)
        // We wrap this to ensure font loading issues don't block the entire UI
        try {
          await Promise.race([
            ensureTemplateFontsLoaded(template),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Font loading timeout')), 3000))
          ]);
        } catch (fontErr) {
          console.warn('Font loading warning (non-fatal):', fontErr);
          // Continue anyway - text might just look wrong but app works
        }
        
        completedItems++;
        updateProgress();

        // 3. Load Images
        if (urls.length > 0) {
          await Promise.all(urls.map(async (url) => {
            try {
              // Use custom cache-busted fetcher instead of native Image
              // This ensures the image blob is in our custom cache for subsequent renders
              await fetchImageBlobUrl(url);
            } catch (error) {
              console.warn(`Failed to preload image: ${url}`, error);
            } finally {
              completedItems++;
              updateProgress();
            }
          }));
        }

        if (isMounted) {
          setState({ loaded: true, progress: 100, error: null });
        }
      } catch (err: any) {
        if (isMounted) {
          console.error("Asset loading failed:", err);
          // Even on error, we might want to let the user in, or show error state
          // For now, let's just mark loaded so they can edit even if broken
          setState({ loaded: true, progress: 100, error: err.message });
        }
      }
    };

    loadAssets();

    return () => {
      isMounted = false;
    };
  }, [template, ensureTemplateFontsLoaded, state.loaded]);

  return state;
};
